/*
 * BelegEiner.tsx - Beleg anzeigen
 *
 * Created by Dr. Maximillian Dornseif 2021 in huWaWi3
 * Copyright (c) 2021, 2022, 2023 HUDORA GmbH
 */

import { CommandBar } from '@fluentui/react'
import { MessageBar } from '@fluentui/react/lib/MessageBar'
import { Panel, PanelType } from '@fluentui/react/lib/Panel'
import { LinkRender } from '../lib/hd-link'
import { KundenCard, LadeChecker } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { Card, CardContent, Typography } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { dateTimeifyTyped } from 'any-to-luxon'
import { useDialog, usePanel, useTitle } from 'fluentui-hooks'
import get from 'lodash.get'
import React, { useState } from 'react'
import { useQuery } from 'urql'

import { usePdfDialog3 } from '../components/PdfDialog3'
import { graphql } from '../gql/gql'
import { EntityPageInner3 } from '../lib/components/ke/EntityPageInner3'
import { useRohdatenPanel3 } from '../lib/hooks/useRohdatenPanel3'
import { useHdPanel } from '../WaWi/components/HdPanel'
import { BELEG_DELIVERY_QUERY } from './BelegeApp'
import { RechungskorrekturErstellen } from './BelegeRechnungskorrektur'

const SCHEMAMAP = {
  Rechnung: 'BRechnung',
  Auftragsbestätigung: 'BAuftrag',
  Bestellbestätigung: 'BAuftrag',
  Zollrechnung: 'BAuftrag',
  Rückmeldung: 'BKommiRueckmeldung',
  Kommissionierbeleg: 'BKommissionierbeleg',
  Vorablieferschein: 'BLieferschein',
  Lieferschein: 'BLieferschein',
}

export const BelegEiner = (props) => {
  const schemaName = SCHEMAMAP[props.entity.belegart] || 'BBeleg'
  const { openDialog: openShowPdfDialog, renderDialog: renderShowPdfDialog } = useHdPanel(
    `PDF ${props.entity.designator}`
  )
  const [renderPdfCommandBarItem, renderPdfPanel] = usePdfDialog3({
    entity: props.entity,
  })
  const [korrekturMessage, setKorrekturMessage] = React.useState(
    <MessageBar styles={{ root: { visibility: 'hidden' } }} />
  )

  const [korrekturpanelNullmengen, setKorrekturpanelNullmengen] = React.useState(false)
  const [openKorrekturPanel, korrekturPanelProps, , dismissKorrekturPanel] = usePanel(
    'Rechnungskorrektur erstellen'
  )

  const commandBarItems = [
    {
      key: 'pdf',
      text: 'PDF anzeigen',
      ariaLabel: 'PDF anzeigen',
      iconProps: { iconName: 'PDF' },
      onClick: openShowPdfDialog,
    },
    renderPdfCommandBarItem,
  ]
  if (props.entity?.belegart === 'Rechnung' || props.entity?.belegart === 'Sammelrechnung') {
    commandBarItems.push({
      key: 'Rechnungskorrektur',
      text: 'Rechnungskorrektur',
      ariaLabel: 'Rechnungskorrektur',
      iconProps: { iconName: 'EraseTool' },
      subMenuProps: {
        items: [
          {
            key: 'RechnugskorrekturNormal',
            text: 'mit Mengen',
            ariaLabel: 'mit Mengen',
            iconProps: { iconName: 'EraseTool' },
            onClick: () => {
              setKorrekturpanelNullmengen(false)
              openKorrekturPanel()
            },
          },
          {
            key: 'RechnugskorrekturGenullt',
            text: 'mit Nullmengen',
            ariaLabel: 'mit Nullmengen',
            iconProps: { iconName: 'ClearSelectionMirrored' },
            onClick: () => {
              setKorrekturpanelNullmengen(true)
              openKorrekturPanel()
            },
          },
        ],
      },
    })
  }

  return (
    <Stack>
      {renderPdfPanel}
      {renderShowPdfDialog({
        renderBody: () => <DisplayPdf designator={props.entity.designator} />,
      })}
      {korrekturMessage}
      <>
        <Panel {...korrekturPanelProps} type={PanelType.custom} customWidth={'60em'}>
          <RechungskorrekturErstellen
            key={`korrekturPanelNull${korrekturpanelNullmengen}`}
            beleg={props.entity}
            nullmengen={korrekturpanelNullmengen}
            onClose={(message) => {
              setKorrekturMessage(message)
              dismissKorrekturPanel()
            }}
          ></RechungskorrekturErstellen>
        </Panel>
      </>
      <Stack direction={'row'} spacing="1em"></Stack>
      <EntityPageInner3
        entity={props.entity}
        schemaName={schemaName}
        commandBarItems={commandBarItems}
        renderEntity={(e, s) => (
          <Stack spacing="0.1em">
            <BelegKopf beleg={props.entity}></BelegKopf>
            <BelegPositionen beleg={props.entity} />
            <BelegFussFaktura beleg={props.entity} />
          </Stack>
        )}
        renderAnmerkung={(e) => null}
        renderRightSidebar={(e, s) => (
          <Stack spacing="1em">
            <KundenCard nr={e.kunde.designator} />
            <LogistikCard entity={e} />
          </Stack>
        )}
        renderBottom={(e, s) => <BelegDelivery nr={props.entity.designator} />}
      />
    </Stack>
  )
}

interface IBelegComandBarProps {
  beleg: any
  data: unknown // Rohdaten zum Debugging
  schemaName: string
  pdfUrl: string
}

function toDateStr(str: string) {
  const dobj = dateTimeifyTyped(str)
  return dobj?.toFormat('yyyy-MM-dd')
}

function BelegKopf(props) {
  useTitle(props.beleg.designator)
  return (
    <div>
      <div>Für {props.beleg.empfaenger}</div>

      <div>
        {props.beleg.bezug?.rechnung?.nr ? (
          <span>
            Zur Rechnung <LinkRender value={props.beleg.bezug.rechnung.nr} />/ vom{' '}
            {toDateStr(props.beleg.bezug.rechnung.datum)}
          </span>
        ) : (
          <span>
            Zu Auftrag <LinkRender value={props.beleg.bezug.auftrag.nr} />/
            <code>{props.beleg.bezug.auftrag.auftragsnr_kunde}</code> vom{' '}
            {toDateStr(props.beleg.bezug.auftrag.datum)}
          </span>
        )}
        {props.beleg.infotext_kunde ? (
          <>
            <br />
            <span>
              Info: <em>{props.beleg.infotext_kunde}</em>
            </span>
          </>
        ) : null}
        {props.beleg.logistik ? (
          <>
            <br />
            <span>
              {props.beleg?.logistik?.paletten?.toFixed(2)} Paletten{', '}
              {props.beleg?.logistik?.packstuecke?.toFixed(2)} Packstücke{', '}
              {props.beleg?.logistik?.gewicht_brutto?.toFixed(2)} kg Brutto
            </span>
          </>
        ) : null}
      </div>

      <Stack direction={'row'} spacing="1em">
        <Adresse name="Lieferung an" adr={props.beleg.lieferadresse} />
        <Adresse name="Rechnung an" adr={props.beleg.rechnungsadresse} />
        <Bezug bezug={props.beleg.bezug} />
      </Stack>
      <Referenzen beleg={props.beleg} />
    </div>
  )
  // TODO: Rechnungsadresse
}

const Adresse = (props) => {
  if (!props.adr) return null
  return (
    <div>
      <b>{props.name}:</b> <LinkRender value={props.adr.designator} />
      <br />
      {props.adr.adresse.name}
      <br />
      {props.adr.adresse.zusatz} {props.adr.adresse.iln}
      {props.adr.adresse.zusatz || props.adr.adresse.iln ? <br /> : null}
      {props.adr.adresse.strasse}
      <br />
      {props.adr.adresse.land} {props.adr.adresse.plz} {props.adr.adresse.ort}
    </div>
  )
}

const Bezug = ({ bezug }) => {
  if (!bezug) return null
  return (
    <div>
      <b>Bezüge:</b>
      <br />

      <BezugHelfer subBezug={bezug.auftrag} title={'Auftrag'} seeAlso={bezug.auftragsnummern} />
      <BezugHelfer
        subBezug={bezug.kommiauftrag}
        title={'Kommiauftrag'}
        seeAlso={bezug.kommiauftragsnummern}
      />
      <BezugHelfer subBezug={bezug.lieferschein} title={'Lieferschein'} seeAlso={bezug.lieferscheinnummern} />
      <BezugHelfer subBezug={bezug.rechnung} title={'Rechnung'} />
      <BezugHelfer subBezug={bezug.intern} title={'Interne Referenz'} />
      <BezugHelfer subBezug={bezug.kunde} title={'Kunden-Referenz'} />
    </div>
  )
}

function BezugHelfer({
  subBezug,
  title,
  seeAlso,
}: {
  subBezug: Record<string, any>
  title: string
  seeAlso?: string[]
}) {
  return subBezug ? (
    <div>
      {title}: <LinkRender value={subBezug.nr} />
      {subBezug.auftragsnr_kunde ? <> / {subBezug.auftragsnr_kunde}</> : null}
      {subBezug.datum ? ` vom ${toDateStr(subBezug.datum)}` : null}
      {seeAlso && seeAlso.length > 0 ? (
        <span>
          {' '}
          und{' '}
          {seeAlso
            .map<React.ReactNode>((nr) => <LinkRender value={nr} />)
            .reduce((prev, curr) => [prev, ', ', curr])}
        </span>
      ) : null}
    </div>
  ) : null
}

const Referenzen = ({ beleg }) => {
  if (!beleg) return null
  return (
    <div>
      <b>Referenzen:</b>{' '}
      {beleg?.kunde?.steuerreferenznr ? <span>Steuer№ Kunde: {beleg?.kunde.steuerreferenznr}</span> : null}{' '}
      {beleg?.kunde?.iln ? <span>, ILN Kunde: {beleg?.kunde.iln}</span> : null}{' '}
      <ReferenzenVerband beleg={beleg} />
    </div>
  )
}

const ReferenzenVerband = ({ beleg }) => {
  return beleg.verband?.nr && beleg.verband?.nr !== 'SV90001' ? (
    <div>
      Verband: {beleg.verband?.name} <LinkRender value={beleg.verband?.nr} />, Mitgliedsnummer:{' '}
      {beleg?.kunde?.kunde_mitgliedsnr_verband}
      {beleg.verband?.unsere_lieferantennr ? (
        <span>unsere Lieferantennummer beim Verband: {beleg.verband?.unsere_lieferantennr}</span>
      ) : null}
    </div>
  ) : null
}

function SuH({ zl }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const onClick = (ev) => setIsExpanded(!isExpanded)

  if (zl) {
    return (
      <>
        <tr>
          <td colSpan={3}>
            <IconButton aria-label="details" onClick={onClick}>
              {isExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </IconButton>
          </td>
          <td title={JSON.stringify(zl.faktura)}>S &amp; H / Zusatzleistungen</td>
          <td />
          <td />
          <td align="right" title="props.faktura.warenwert">
            {isExpanded ? null : <ValueRender value={zl.faktura.warenwert} format="eurocent" />}
          </td>
          <td align="right" title="faktura.abschlag_betrag">
            {isExpanded ? null : (
              <ValueRender value={zl.faktura.positionsabschlaege_betrag} format="eurocent" />
            )}
          </td>
          <td align="right" title="faktura.summe_netto_betraege">
            {isExpanded ? null : <ValueRender value={zl.faktura.summe_netto_betraege} format="eurocent" />}
          </td>
          <td></td>
        </tr>
        {isExpanded
          ? zl.positionen?.map((pos) => <BelegPosition key={pos.bezug?.posnr_auftrag} pos={pos} />)
          : null}
      </>
    )
  }
  return null
}

export const BelegPositionen = ({ beleg }) => {
  return (
    <section>
      <h2>Positionen</h2>
      <table className="hd-mytable" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>Menge</th>
            <th>Einheit</th>
            <th>Item№</th>
            <th>Name</th>
            <th>EAN/GTIN</th>
            {beleg.leistungen.faktura ? (
              <>
                <th>Einzelpreis</th>
                <th>Summe</th>
                <th>Abschlag</th>
                <th>Netto</th>
                <th>Steuersatz</th>
              </>
            ) : (
              <>
                <th>KG</th>
              </>
            )}
            <th>Bezug</th>
          </tr>
        </thead>
        <tbody>
          {beleg.leistungen.positionen?.map((pos) => (
            <BelegPosition key={pos.bezug?.posnr_auftrag} pos={pos} />
          ))}
          <SuH zl={beleg?.zusatzleistungen}></SuH>
        </tbody>
      </table>
    </section>
  )
}

export const BelegPosition = ({ pos }) => {
  return (
    <React.Fragment>
      <tr>
        <td align="right">{pos.menge}</td>
        <td>{pos?.artikel?.einheit ?? 'mal'}</td>
        <td>
          <LinkRender value={pos.itemnr} />
        </td>
        <td>{pos?.artikel?.name ?? pos?.name}</td>
        <td>{pos?.artikel?.ean}</td>
        {pos?.faktura ? (
          <>
            <td align="right">
              <ValueRender value={pos?.einzelpreis} format="eurocent" />
            </td>
            <td align="right">
              <ValueRender value={pos?.faktura?.warenwert} format="eurocent" />
            </td>
            <td align="right">
              {pos?.faktura?.abschlag_betrag} ({pos?.faktura?.abschlag_prozent.toFixed(2)}%{' '}
              {pos?.faktura?.abschlag_text})
            </td>
            <td align="right">
              <ValueRender value={pos?.faktura?.netto_betrag} format="eurocent" />
            </td>
            <td align="right" title="faktura.steuersatz_prozent">
              {pos?.faktura?.steuersatz_prozent} %
            </td>
          </>
        ) : (
          <>
            <td>{pos.logistik.gewicht_brutto?.toFixed(2)} </td>
          </>
        )}
        <td>
          <LinkRender value={pos?.bezug?.auftragsnr} /> {pos?.bezug?.auftragsnr_kunde}{' '}
          {pos?.logistik?.nves ? null : pos?.logistik?.nves?.map((x) => x.nve).join(', ')}
        </td>
      </tr>
      {pos?.infotext_kunde ? (
        <tr>
          <td colSpan={10}>{pos?.infotext_kunde}</td>
        </tr>
      ) : null}
    </React.Fragment>
  )
}

function BelegFussFaktura({ beleg }) {
  if (!beleg.faktura) {
    return null
  }

  return (
    <section>
      <h2>Faktura</h2>
      <table>
        <thead>
          <tr>
            <td></td>
            <th align="right" title="faktura.summe_netto_betraege">
              Positionen Netto
            </th>
            <th align="right" title="faktura.abschlag_betrag">
              Abzüge
            </th>
            <th align="right" title="faktura.abschlag_text"></th>
            <th align="right" title="faktura.netto_betrag">
              Netto
            </th>
            <th align="right" title="faktura.steuer_betrag">
              Steuer
            </th>
            <th align="right" title="faktura.brutto_betrag">
              Brutto
            </th>
          </tr>
        </thead>
        <tbody>
          <SummenZeile zeile={beleg.leistungen} title="Ware" />
          <SummenZeile zeile={beleg.zusatzleistungen} title="S&H/ Zusatzleistungen" />
          <tr>
            <td>Gesamt</td>
            <TdEuroCent data={beleg} propName="faktura.warenwert" />
            <TdEuroCent data={beleg} propName="faktura.abschlag_betrag" />
            <td></td>
            <TdEuroCent data={beleg} propName="faktura.netto_betrag" />
            <TdEuroCent data={beleg} propName="faktura.steuer_betrag" />
            <td align="right" title="faktura.brutto_betrag">
              <b>
                <ValueRender value={beleg.faktura.brutto_betrag} format="eurocent" />
              </b>
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div>
        Der Rechnungsbetrag von <ValueRender value={beleg.faktura.brutto_betrag} format="eurocent" />{' '}
        beinhaltet
        {beleg.faktura.steuer?.map((s) => (
          <SteuerAnzeige key={s.steuersatz_prozent} steuer={s} />
        ))}
      </div>
      <div>
        Zahlung innerhalb von {beleg.konditionen.zahlungs_tage} Tagen netto/netto oder in{' '}
        {beleg.konditionen.skonto_tage} Tagen mit {beleg.konditionen.skonto_prozent}% Skonto.
      </div>
    </section>
  )
}

function SummenZeile({ zeile, title }) {
  return (
    <tr>
      <td>{title}</td>
      <TdEuroCent data={zeile} propName="faktura.summe_netto_betraege" />
      <TdEuroCent data={zeile} propName="faktura.abschlag_betrag" />
      <td align="right" title="faktura.abschlag_text">
        {zeile.faktura.abschlag_text}
      </td>
      <TdEuroCent data={zeile} propName="faktura.netto_betrag" />
      <TdEuroCent data={zeile} propName="faktura.steuer_betrag" />
      <TdEuroCent data={zeile} propName="faktura.brutto_betrag" />
      <td></td>
    </tr>
  )
}

function TdEuroCent({ data, propName }) {
  return (
    <td align="right" title={propName}>
      <ValueRender value={get(data, propName)} format="eurocent" />
    </td>
  )
}

function SteuerAnzeige({ steuer }) {
  return (
    <span>
      <ValueRender value={steuer.steuer_betrag} format="eurocent" />
      USt. zu {steuer.steuersatz_prozent.toFixed(2)}%
    </span>
  )
}

const BelegDelivery = (props) => {
  const [{ fetching, error, data }] = useQuery({
    query: BELEG_DELIVERY_QUERY,
    variables: { nr: props.nr },
    pause: !props.nr,
  })
  const deliveryStatus = data?.entity?.deliveryStatus
  const [rohdatenItem, rohdatenDialog] = useRohdatenPanel3({
    entity: deliveryStatus,
    schemaName: '',
    title: 'Auslieferungs-Rohdaten',
  })

  return (
    <div>
      {rohdatenDialog}
      <h2>Auslieferungsstatus</h2>
      <CommandBar items={[]} farItems={[rohdatenItem]} />
      <LadeChecker loading={fetching} error={error} label={`Lade Auslieferungsstatus ${props.nr}`}>
        {deliveryStatus ? (
          <div>
            Auslieferung Stand {deliveryStatus.updated_at}. Beginn war {deliveryStatus.created_at}.
            <table>
              <tbody>
                {deliveryStatus.info?.map((info) => (
                  <BelegDeliveryInfo key={info.art} info={info} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>Bisher keine Auslieferung</div>
        )}
      </LadeChecker>
    </div>
  )
}

// "art": "SammelRechnungskorrektur",
// "started_at": "2021-07-09T21:00:08.693Z",
// "finished_at": "2021-07-09T21:00:09.548Z",

const BelegDeliveryInfo = ({ info }) => {
  if (!info?.deliveries) {
    return null
  }

  return (
    <>
      <tr>
        <th colSpan={3}>{info.art}</th>
        <td>
          <i>{info.finished_at ? `${info.finished_at} erledigt` : null}</i>
        </td>
      </tr>
      {info?.deliveries?.map((d) => (
        <BelegDeliveryInfoDelivery key={d.at} delivery={d} />
      ))}
    </>
  )
}

const BelegDeliveryInfoDelivery = ({ delivery }) => {
  return (
    <tr>
      <td>{delivery.type}</td>
      <td>{delivery.at}</td>
      <td>{delivery.version}</td>
      <td>{delivery.destination}</td>
    </tr>
  )
}

const LogistikCard = ({ entity }) => {
  if (!entity?.logistik?.nves?.length > 0) return null
  if (!entity?.belegart === 'Sammelrechnung') return null
  return (
    <Card>
      <CardContent>
        <section title="T&T Info" className="ttinfo">
          <Typography>
            <Typography color="text.secondary" gutterBottom>
              T&T Informationen
            </Typography>
            <dl>
              {entity.logistik.nves.map((nve) => {
                const skus = entity.leistungen.positionen
                  .map((pos) => pos.logistik.nves.map((n) => (n.nve === nve.nve ? pos.itemnr : null)))
                  .flat()
                  .filter(Boolean)
                return (
                  <>
                    <dt>
                      {' '}
                      <Typography>
                        <LinkRender value={nve.nve} />
                      </Typography>
                    </dt>
                    <dd>
                      <Typography>
                        {nve.art}, {nve.gewicht?.toFixed(2)} kg, {skus.length > 0 ? <br /> : null}
                        {skus.map((sku) => (
                          <>
                            <LinkRender value={sku} />{' '}
                          </>
                        ))}
                      </Typography>
                    </dd>
                  </>
                )
              })}
            </dl>
          </Typography>
        </section>
      </CardContent>
    </Card>
  )
}

const DisplayPdf = ({ designator }) => {
  const [{ fetching: loading, error, data }] = useQuery({
    query: BELEG_PDF_QUERY,
    variables: { nr: designator },
    pause: !designator,
  })

  return (
    <LadeChecker loading={loading} error={error} label={`Lade PDF ${designator}`}>
      {data?.entity?.pdfUrl ? (
        <>
          PDF{' '}
          <a href={data?.entity?.pdfUrl} download>
            hier herunterladen
          </a>{' '}
          oder{' '}
          <a href={data?.entity?.pdfUrl} target="_blank">
            anzeigen
          </a>
          .
          <object data={data?.entity?.pdfUrl} type="application/pdf" width="100%" height="100%">
            <iframe src={data?.entity?.pdfUrl} width="100%" height="100%" />
          </object>
        </>
      ) : null}
    </LadeChecker>
  )
}

const BELEG_PDF_QUERY = graphql`
  query BelegPdf($nr: ID!) {
    entity: beleg(designator: $nr) {
      pdfUrl
    }
  }
`
